import { en } from '@/content/en'
import { ALL_LOCALE_CODES, DEFAULT_LOCALE, localeDef } from './locales'
import { translations } from '@/content'

/**
 * The content contract. Derived from the English file rather than declared
 * separately, so the copy *is* the type: add a section to `content/en.ts` and
 * every locale immediately knows the key exists, with no interface to keep in
 * step. There is exactly one place a string can be introduced.
 */
export type Dictionary = typeof en

/**
 * What a non-English content file may contain.
 *
 * Everything optional, at every depth — because content arrives one page and
 * one language at a time, and a locale that is 40% translated has to render.
 * The alternative (every locale complete or the build fails) means either
 * shipping six languages at once or shipping none, and in practice it means
 * machine-translating to satisfy the compiler.
 *
 * **Arrays are atomic.** A half-supplied list of features is not a partial
 * translation, it is a broken page — so a locale either gives the whole array
 * or omits it and inherits English. This also keeps the merge honest: no
 * index-wise zipping of two lists that may not describe the same things.
 */
export type Translation<T = Dictionary> = T extends readonly unknown[]
  ? T
  : T extends object
    ? { [K in keyof T]?: Translation<T[K]> }
    : T

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * English underneath, the locale's own strings on top.
 *
 * Deliberately not a shallow spread: sections nest, and a shallow merge would
 * mean supplying a whole section to translate one line of it — which is how
 * translated pages end up with English headings restored by a later edit.
 */
function mergeInto<T>(base: T, over: unknown): T {
  if (over === undefined) return base
  if (!isPlainObject(base) || !isPlainObject(over)) return over as T

  const out: Record<string, unknown> = { ...base }
  for (const key of Object.keys(base)) {
    out[key] = mergeInto((base as Record<string, unknown>)[key], over[key])
  }
  return out as T
}

const cache = new Map<string, Dictionary>()

/**
 * The dictionary a page renders from. English for `en`; for anything else,
 * English with that locale's strings laid over it.
 *
 * Cached because prerendering walks every route × every locale and the merge is
 * pure — same locale, same object.
 */
export function dictionaryFor(locale: string): Dictionary {
  const cached = cache.get(locale)
  if (cached) return cached

  const resolved =
    locale === DEFAULT_LOCALE ? en : mergeInto(en, translations[locale] as unknown)
  cache.set(locale, resolved)
  return resolved
}

/**
 * How much of a locale is actually written, counting leaf strings.
 *
 * This exists to be *reported*, not enforced. The Website Preview panel in
 * Excellence shows it so "Spanish is at 30%" is a fact somebody can act on,
 * rather than something discovered by a Spanish-speaking prospect. An
 * untranslated leaf is not a bug — it is English, on purpose, until someone
 * writes it.
 */
export interface LocaleCoverage {
  locale: string
  /** Launch tier from the locale registry — 1 blocks the 1 September launch. */
  tier: 1 | 2 | 3
  translated: number
  total: number
  /** 0–1. English is 1 by definition: it is the base, not a translation of it. */
  ratio: number
  /** Dotted paths still inheriting English, for whoever is filling them in. */
  missing: string[]
}

function walk(
  base: unknown,
  over: unknown,
  path: string,
  acc: { translated: number; total: number; missing: string[] },
): void {
  if (isPlainObject(base)) {
    for (const key of Object.keys(base)) {
      const next = isPlainObject(over) ? over[key] : undefined
      walk(base[key], next, path ? `${path}.${key}` : key, acc)
    }
    return
  }
  // A leaf: a string, a number, or an atomic array.
  acc.total += 1
  if (over === undefined) acc.missing.push(path)
  else acc.translated += 1
}

export function coverageFor(locale: string): LocaleCoverage {
  if (locale === DEFAULT_LOCALE) {
    const acc = { translated: 0, total: 0, missing: [] as string[] }
    walk(en, en, '', acc)
    return {
      locale,
      tier: localeDef(locale).tier,
      translated: acc.total,
      total: acc.total,
      ratio: 1,
      missing: [],
    }
  }

  const acc = { translated: 0, total: 0, missing: [] as string[] }
  walk(en, translations[locale], '', acc)
  return {
    locale,
    tier: localeDef(locale).tier,
    translated: acc.translated,
    total: acc.total,
    ratio: acc.total === 0 ? 1 : acc.translated / acc.total,
    missing: acc.missing,
  }
}

export function allCoverage(): LocaleCoverage[] {
  // The WHOLE roster, paused locales included — see `LocaleDef.live`. This
  // number is how anyone learns a paused locale is ready to come back, so
  // dropping it to the live list would remove the only signal for un-pausing.
  return ALL_LOCALE_CODES.map(coverageFor)
}
